// Shared client/server protocol. Imported by both the Worker and web/.

/**
 * Path the app is mounted on: it is served as part of the WorkBrawl hub at
 * https://workbrawl.com/snakebrawl/. Static files live in public/snakebrawl/ to match.
 */
export const BASE_PATH = "/snakebrawl";

/** People per room. PLAYER_COLORS must have at least this many colors. */
export const MAX_PLAYERS = 12;

/** Arena size in cells. Edges wrap around: there are no walls. */
export const GRID_W = 36;
export const GRID_H = 24;
/** One simulation step. Every step is broadcast, so this is also the frame rate. */
export const TICK_MS = 120;

/** Round lengths the room creator can pick, in seconds. */
export const DURATIONS = [120, 180, 300] as const;
export type Duration = (typeof DURATIONS)[number];
export const DEFAULT_DURATION: Duration = 180;

export const COUNTDOWN_MS = 3000;
/**
 * Fruit kinds, by index: two everyday ones and a rarer one worth more. The frontend picks
 * how each one looks (web/render.ts).
 */
export const FRUIT_POINTS = [1, 1, 3] as const;
export type FruitKind = 0 | 1 | 2;
/** The kind that is worth more than the others. */
export const BONUS_FRUIT: FruitKind = 2;
/** Points for the snake another one crashed into. */
export const CRASH_POINTS = 3;

/** 0 up, 1 right, 2 down, 3 left. Opposites differ by 2. */
export type Dir = 0 | 1 | 2 | 3;

/**
 * lobby: waiting for someone to start. countdown: 3-2-1 before a round.
 * running: snakes move and the clock counts down. finished: ranking on screen.
 */
export type RoomStatus = "lobby" | "countdown" | "running" | "finished";

// Mid-tone colors that keep contrast on both light and dark backgrounds. Indexed by slot.
export const PLAYER_COLORS = [
  "#e5484d", // red
  "#0b84e5", // blue
  "#2f9e44", // green
  "#f08c00", // orange
  "#8e4ec6", // purple
  "#0f9d9a", // teal
  "#d6409f", // pink
  "#a0703c", // brown
  "#c79a00", // gold
  "#5b5bd6", // indigo
  "#7aa116", // lime
  "#6b7785", // slate
];

export interface PlayerInfo {
  id: string;
  name: string;
  /** 0..MAX_PLAYERS-1: picks the color and identifies the snake in tick messages. */
  slot: number;
  score: number;
  online: boolean;
}

export interface RankingEntry {
  id: string;
  name: string;
  slot: number;
  score: number;
}

export type ClientMsg = { type: "dir"; dir: Dir } | { type: "start" } | { type: "ping" };

/** JSON body of POST /api/rooms. */
export interface CreateRoomRequest {
  duration?: Duration; // omitted = DEFAULT_DURATION
  turnstileToken: string;
}

/** JSON body of POST /api/rooms/:code/verify (join by code from the home screen). */
export interface VerifyRoomRequest {
  turnstileToken: string;
}

/** Response of GET /api/config. */
export interface ClientConfig {
  turnstileSiteKey: string;
}

/** Error codes returned as `{ error }` by the HTTP API. */
export type ApiErrorCode = "INVALID_REQUEST" | "TURNSTILE_FAILED" | "ROOM_NOT_FOUND";

export type ServerMsg =
  | {
      type: "state";
      you: string;
      status: RoomStatus;
      /** Round length in seconds. */
      duration: Duration;
      /** Milliseconds left in the countdown (status "countdown") or the round ("running"). */
      remainingMs: number;
      players: PlayerInfo[];
      /** Last finished round, best first. Empty before the first one ends. */
      ranking: RankingEntry[];
    }
  | {
      /** One simulation step: the whole arena, small enough to send every time. */
      type: "tick";
      /** Milliseconds left in the round. */
      t: number;
      /** [slot, cells head-first]. Cells are x + y * GRID_W. Missing slot = waiting to respawn. */
      s: [number, number[]][];
      /** Fruit on the arena: [cell, kind]. */
      f: [number, FruitKind][];
      /** [slot, score] for every player in the round. */
      p: [number, number][];
    }
  | { type: "player_joined"; player: PlayerInfo }
  | { type: "player_updated"; player: PlayerInfo }
  | { type: "player_left"; id: string }
  | { type: "error"; code: ErrorCode; message: string }
  | { type: "pong" };

/**
 * Exact keep-alive frames. The room registers them as a WebSocket auto-response, so
 * Cloudflare answers pings at the edge WITHOUT waking the Durable Object: an idle room
 * with open tabs stays hibernated (no billable duration). The client must send PING_FRAME
 * byte for byte, or the ping falls through to the room and wakes it.
 */
export const PING_FRAME = '{"type":"ping"}';
export const PONG_FRAME = '{"type":"pong"}';

export type ErrorCode = "ROOM_FULL" | "ROOM_NOT_FOUND" | "BAD_MESSAGE";

export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;

const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_RE.test(code);
}

export function isValidPlayerId(id: string): boolean {
  return UUID_RE.test(id);
}

export function isDuration(value: unknown): value is Duration {
  return (DURATIONS as readonly unknown[]).includes(value);
}

export function isDir(value: unknown): value is Dir {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

/**
 * Trims and collapses whitespace. Returns null when the name is empty, longer than
 * NAME_MAX_LENGTH characters, or contains HTML-ish or control characters.
 */
export function sanitizeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  const length = [...name].length;
  if (length < 1 || length > NAME_MAX_LENGTH) return null;
  if (/[<>&"'`\p{Cc}]/u.test(name)) return null;
  return name;
}

/** Best score first; ties keep the lower slot (who joined earlier) ahead. */
export function rank<T extends { score: number; slot: number }>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => b.score - a.score || a.slot - b.slot);
}
